/**
 * Pinia artwork-repository store (Phase F PR-4 — frontend wiring).
 *
 * Card: "Phase F - Artwork Repository" -- https://trello.com/c/hYlwyKYT
 * Pebble: Phase F-4: frontend wiring (Pinia store + lifecycle resolver +
 *   inspector footer attribution).
 *
 * Hydrates two collections for the tenant:
 *
 *   - artworks  : raw artwork files with ``lifecycle_state`` (the
 *     state-machine column landed in F-1 + F-2; transitions land via
 *     the F-3 custom endpoint, see ``transition()`` below).
 *   - repositories : tenant-scoped folders that own the artworks. Listed
 *     via the F-3 ``GET /api/artwork_repositories/mine`` convenience
 *     alias so callers don't need to construct a tenant filter param.
 *
 * Variant linkage (F-4 forward-compat)
 * -------------------------------------
 *
 * Today's ``Variant`` row carries ``artwork_file_id`` (a free-form
 * S3-shaped string), not an FK to ``artworks.id`` — the formal
 * relationship is a later Phase F PR. Until then we match by string
 * equality between ``variant.artwork_file_id`` and ``artwork.id`` (cast
 * to string on both sides) with a secondary match on filename. When
 * neither matches, callers fall back to the demo hash in
 * ``productLifecycle.js``.
 *
 * Why a separate store (not bolted onto productEditor):
 *   - Artworks are tenant-scoped, not campaign_product-scoped. The same
 *     Artwork rows hydrate the editor AND the (future) Decoration
 *     Library shell.
 *   - The productEditor store's serverState is keyed to ONE
 *     campaign_product id; swapping products bumps a load token and
 *     discards in-flight requests. The artwork list outlives any single
 *     product's editor session.
 *   - The F-3 endpoints don't compose with the existing JSON:API include
 *     path (no Variant->Artwork FK yet); a sibling store keeps the wire
 *     contract explicit.
 *
 * Empty-tenant + true-SA model:
 *   - Owner / Builder / SE: the SAFRS list + ``/mine`` both filter to the
 *     acting tenant via ``RoleMixin``.
 *   - True SA (non-impersonating): both endpoints return all tenants'
 *     rows. ``getArtworkForVariant`` doesn't tenant-filter on the client
 *     because the variant's parent ``decoration->campaign_product`` is
 *     already tenant-scoped at the read boundary.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/services/api'
import { buildImageUrl as sharedBuildImageUrl } from '@/utils/imageBaseUrl'
import { extensionOf } from '@/utils/artworkUploadContract'
import { emitSidebarRefresh } from '@/stores/sidebarRefresh'

export const useArtworksStore = defineStore('artworks', () => {
    // ---- state ----
    const artworks = ref([])
    const repositories = ref([])
    const loading = ref(false)
    const error = ref(null)
    // Epoch guard: bumped on clear() so a late-arriving fetch from a
    // previous tenant cannot repopulate the store after impersonation
    // stops. Mirrors the distributorSummary store's pattern (PR #1402
    // bar-raiser P2).
    let epoch = 0

    // ---- getters / lookup helpers ----

    /**
     * Look up an Artwork by id (string or numeric).
     *
     * The Artwork.id is a SERIAL integer at the DB layer; callers may
     * hold either an integer or a string copy. Normalising both sides
     * to string keeps the match robust against API serializers that
     * stringify ids (SAFRS does) and ad-hoc client-side coercion.
     */
    function getArtworkById(id) {
        if (id == null) return null
        const target = String(id)
        return artworks.value.find((a) => String(a.id) === target) ?? null
    }

    /**
     * Look up a Repository by id.
     */
    function getRepository(id) {
        if (id == null) return null
        const target = String(id)
        return repositories.value.find((r) => String(r.id) === target) ?? null
    }

    /**
     * Resolve the Artwork record (if any) for a decoration variant.
     *
     * Match precedence:
     *   1. ``variant.artwork_file_id`` <==> ``artwork.id`` (string-eq).
     *      This is the forward-compat match: when Variant gains a real
     *      FK to ``artworks.id`` (later Phase F PR), this branch is the
     *      one that lights up. Until then it lights up only when the
     *      ``artwork_file_id`` happens to be a numeric string matching
     *      an existing Artwork pkey — i.e., in seeded fixtures or by
     *      coincidence in production. That's fine: the demo hash
     *      fallback covers the long tail.
     *   2. ``variant.artwork_filename`` <==> ``artwork.filename`` (eq).
     *      Secondary heuristic for seed shapes where the variant's
     *      ``artwork_file_id`` is an S3 UUID but the filename happens
     *      to align. Skipped when either side is empty.
     *
     * Returns ``null`` when no match — callers MUST treat this as "no
     * real lifecycle data; fall back to the demo hash."
     */
    function getArtworkForVariant(variant) {
        if (!variant || typeof variant !== 'object') return null
        // Primary: id match.
        if (variant.artwork_file_id != null && variant.artwork_file_id !== '') {
            const byId = getArtworkById(variant.artwork_file_id)
            if (byId) return byId
        }
        // Secondary: filename match.
        const fname = variant.artwork_filename
        if (typeof fname === 'string' && fname.trim()) {
            const target = fname.trim()
            const byName = artworks.value.find(
                (a) => typeof a.filename === 'string' && a.filename === target,
            )
            if (byName) return byName
        }
        return null
    }

    /**
     * Convenience: resolve the real Artwork for a variant and return
     * its ``lifecycle_state``, or ``null`` when there's no match.
     *
     * Callers (productLifecycle.js) use a null return as the signal to
     * fall back to the demo hash rule.
     */
    function lifecycleStateForVariant(variant) {
        const a = getArtworkForVariant(variant)
        return a ? a.lifecycle_state : null
    }

    /**
     * Set the cached ``lifecycle_state`` for the Artwork resolved from a
     * variant, in place, so consumers re-render WITHOUT a refetch.
     *
     * Issue #2179 (Problem 1 state-refresh): after a successful
     * send-for-approval the product editor must reflect the new
     * ``awaiting_approval`` lifecycle immediately -- the placement chip
     * and the VariantCard's Send-reminder label both key off the
     * resolver, which reads this cached ``lifecycle_state``. The send
     * endpoint has already transitioned ``Artwork.pipeline_state``
     * server-side; this mirrors that into the client cache without a
     * round trip.
     *
     * No-op (returns ``false``) when the variant does not resolve to a
     * cached Artwork -- the caller falls back to whatever refresh path
     * it has. Returns ``true`` when the cached row was updated.
     */
    function setLifecycleStateForVariant(variant, state) {
        const a = getArtworkForVariant(variant)
        if (!a) return false
        a.lifecycle_state = state
        return true
    }

    // ---- actions ----

    /**
     * Hydrate ``artworks`` + ``repositories`` for the effective tenant.
     *
     * Two round trips in parallel:
     *   - GET /api/artworks/         (SAFRS list, tenant-filtered)
     *   - GET /api/artwork_repositories/mine  (F-3 convenience alias)
     *
     * On error both collections reset to ``[]`` so consumers fall back
     * to the demo hash + default attribution rather than render stale
     * data from a prior tenant.
     *
     * Epoch-guarded: if ``clear()`` runs while the fetch is in flight
     * (e.g., impersonation stop), the late response is dropped on the
     * floor. Same pattern as distributorSummary store.
     */
    async function fetch() {
        const myEpoch = epoch
        loading.value = true
        error.value = null
        try {
            const [artworksRes, reposRes] = await Promise.all([
                api.get('/artworks/'),
                api.get('/artwork_repositories/mine'),
            ])
            if (myEpoch !== epoch) return
            artworks.value = normalizeArtworksResponse(artworksRes.data)
            repositories.value = normalizeRepositoriesResponse(reposRes.data)
        } catch (e) {
            if (myEpoch !== epoch) return
            error.value = e.message || 'artworks fetch failed'
            artworks.value = []
            repositories.value = []
        } finally {
            if (myEpoch === epoch) loading.value = false
        }
    }

    /**
     * Upload a new Artwork. Two-step:
     *
     *   1. POST /api/upload_image (multipart) -> { uuid }. The S3 upload
     *      is owned by the existing upload_image endpoint (same one the
     *      product editor + carousel slides use).
     *   2. POST /api/artworks/ with the JSON:API envelope SAFRS expects:
     *      ``{data: {type: 'Artwork', attributes: {...}}}``. The
     *      ``filename`` is the local filename the user picked (so the
     *      library list stays human-readable); the S3 UUID is appended
     *      so the file can be resolved later when the Variant<->Artwork
     *      FK lands.
     *
     * On success the new Artwork row is prepended to the in-memory list
     * so the Decoration Library grid re-renders immediately without a
     * full refetch. Errors propagate to the caller.
     */
    async function uploadArtwork({
        file,
        filename,
        repositoryId,
        inkColorHex = null,
        distributorId = null,
        companionFile = null,
        priorApprovalClientId = null,
        priorApprovalMemberId = null,
        checkDuplicates = false,
        allowDuplicate = false,
    }) {
        if (!file) throw new Error('uploadArtwork: file is required')
        if (!repositoryId) throw new Error('uploadArtwork: repositoryId is required')
        if (priorApprovalClientId != null && priorApprovalMemberId != null) {
            throw new Error(
                'uploadArtwork: name a client or a team member as the prior ' +
                    'approver, not both',
            )
        }
        const safeName = filename || file.name || 'artwork'

        // The backend upload endpoint validates by filename EXTENSION
        // (extension is authoritative -- see api/src/endpoints/upload.py).
        // ``safeName`` is often a human display name with no extension
        // (AddArtworkWizard pre-fills it sans ext), which the backend
        // rejects with a 400. Send a multipart filename that always
        // carries the real extension from the File object, while the
        // human display name persisted to the row stays as ``safeName``.
        const uploadName = extensionOf(safeName)
            ? safeName
            : `${safeName}${extensionOf(file.name || '')}`

        // Capture the epoch at entry so a clear() mid-flight (impersonation
        // stop) drops the late prepend on the floor. Mirrors fetch().
        const myEpoch = epoch

        // Step 1: multipart upload to S3 via the shared upload_image endpoint.
        const formData = new FormData()
        formData.append('file', file, uploadName)
        // No per-call Content-Type: the shared instance strips it for any
        // FormData body so the browser sets multipart/form-data + boundary.
        const uploadRes = await api.post('/upload_image', formData)
        const uuid = uploadRes?.data?.uuid

        // Step 1a (Bug Intake #310): is this exact file already in the
        // caller's library? The upload returned the SHA-256 of the bytes it
        // stored; the lookup is confined to the caller's own tenant on the
        // server. A match HOLDS the upload (nothing is created) so the
        // surface can offer reuse; ``allowDuplicate`` is the user's explicit
        // "upload as a new copy". Opt-in per caller, and best-effort: a
        // failed lookup never blocks an upload. The S3 object stays as the
        // accepted orphan the upload endpoint documents.
        const contentHash = uploadRes?.data?.content_hash
        if (checkDuplicates && !allowDuplicate && contentHash) {
            const duplicates = await findDuplicateArtworks(contentHash)
            if (duplicates.length > 0) throw duplicateUploadError(duplicates)
        }

        // Step 1b (PR-4b, companion DST): an .emb upload MAY carry a companion
        // machine .dst so the backend renders the companion locally (free)
        // instead of the paid Wilcom fallback. Upload it as a second S3 object
        // and build its image URL; the URL threads to the backend via
        // process_assets below (Step 3). Optional -- absent -> the backend
        // uses the paid Wilcom render. A companion upload failure is
        // soft-degraded (log + continue with no companion) so it never fails
        // the primary artwork upload; the worst case is a paid render.
        let companionSourceUrl = null
        if (companionFile) {
            try {
                const companionForm = new FormData()
                companionForm.append(
                    'file',
                    companionFile,
                    companionFile.name || 'companion.dst',
                )
                const companionRes = await api.post(
                    '/upload_image',
                    companionForm,
                )
                const companionUuid = companionRes?.data?.uuid
                companionSourceUrl = companionUuid
                    ? buildImageUrl(companionUuid)
                    : null
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn(
                    'companion DST upload failed; falling back to paid render:',
                    err?.message || err,
                )
                companionSourceUrl = null
            }
        }

        // Step 2: create the Artwork row. We keep the human filename in
        // the ``filename`` column; the S3 uuid is encoded in the suffix
        // so downstream resolvers can map back to the asset. When the
        // formal Variant.artwork_id FK lands the suffix becomes
        // unnecessary, but for now it's the only carry-channel.
        const persistedFilename = uuid ? `${safeName}#${uuid}` : safeName
        // PR-D: also persist a first-class image_url + mime type so
        // grid + drawer surfaces can render <img> directly without
        // re-parsing the filename suffix. The URL is built from the
        // S3 base + uuid (matches the existing ``getImage`` mixin
        // pattern at frontend/src/mixins/helpers.js).
        const imageUrl = uuid ? buildImageUrl(uuid) : null
        const imageMimeType = file?.type || null
        const attributes = {
            repository_id: repositoryId,
            filename: persistedFilename,
            original_filename: uploadName,
            image_url: imageUrl,
            image_mime_type: imageMimeType,
            ink_color_hex: inkColorHex,
            lifecycle_state: 'draft',
        }
        // Tenant-scoped callers (Owner/Builder/SE/impersonating SA) let the
        // backend before_insert auto-stamp distributor_id from the caller's
        // effective tenant -- omit it. A NON-impersonating Super Admin has no
        // effective tenant, so the auto-stamp no-ops and the NOT NULL insert
        // fails; such callers (e.g. the SA-gated Artifi prototype) must pass
        // an explicit distributorId, which the model's @validates bypass
        // allows for a true SA. The repository MUST belong to that tenant.
        if (distributorId != null) {
            attributes.distributor_id = distributorId
        }
        const body = { data: { type: 'Artwork', attributes } }
        const createRes = await api.post('/artworks/', body)
        const created = normalizeArtworksResponse(createRes.data)[0] || null
        // Drop the prepend if a clear() landed between the dispatch and
        // the response -- the row belongs to a tenant we've left.
        if (myEpoch !== epoch) return created

        // Step 2b (Bug Intake #332, GH #4292): "This file was already
        // approved by [client|team member]" on a .dst/.emb. Record that
        // prior approval now, while the artwork is unused -- the backend
        // refuses it once a decoration exists. A refusal does NOT undo the
        // upload: the artwork stays (prepended below) and the caller gets an
        // error naming it, so it can tell the user the file goes through
        // normal approval instead of re-uploading a duplicate.
        let priorApprovalError = null
        const priorApprovalBody =
            priorApprovalMemberId != null
                ? { team_member_id: Number(priorApprovalMemberId) }
                : priorApprovalClientId != null
                    ? { client_id: Number(priorApprovalClientId) }
                    : null
        if (priorApprovalBody && created?.id != null) {
            try {
                await api.post(
                    `/artworks/${created.id}/prior-approval`,
                    priorApprovalBody,
                )
            } catch (err) {
                const reason =
                    err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    err?.message ||
                    'unknown error'
                const who =
                    priorApprovalMemberId != null ? 'team member' : 'client'
                priorApprovalError = new Error(
                    `The file was uploaded, but its earlier ${who} approval ` +
                        `could not be recorded: ${reason} It will need to be ` +
                        `sent for approval.`,
                )
                priorApprovalError.uploadedArtwork = created
            }
        }

        // Step 3 (Artifi Phase A): fire-and-forget call to the asset
        // pipeline endpoint. This is what populates Artwork.thumbnail_url
        // (so the library grid renders an actual thumbnail) and
        // Artwork.artifi_unique_name (so /api/artifi/preview can render a
        // composited preview in the Stage). The call is BEST-EFFORT --
        // a failure here is logged but doesn't fail the upload, because
        // the backend already returns a structured 404 + placeholder
        // when the Artifi columns are NULL. The frontend grid falls
        // back through thumbnail_url -> image_url -> placeholder, so a
        // worst-case failure is a missing thumbnail until the backfill
        // script runs. Patches the local copy with the populated columns
        // so the grid renders the real thumb without a refetch.
        if (created?.id != null) {
            try {
                const processRes = await api.post(
                    `/artworks/${created.id}/process_assets`,
                    // Thread the companion DST URL (PR-4b) so the backend
                    // records it on the preview sidecar for the free render.
                    companionSourceUrl
                        ? { preview_source_url: companionSourceUrl }
                        : {},
                )
                const processed = processRes?.data
                if (processed) {
                    if (processed.thumbnail_url) {
                        created.thumbnail_url = processed.thumbnail_url
                    }
                    if (processed.artifi_unique_name) {
                        created.artifi_unique_name = processed.artifi_unique_name
                    }
                    // Bug Intake #716: the file's shape, stamped by this
                    // pipeline run (null = unknown). The create answer has
                    // none, so without it an upload dialog keeps a row whose
                    // shape reads as unknown until the library is refetched.
                    if ('aspect_ratio' in processed) {
                        created.aspect_ratio = processed.aspect_ratio
                    }
                }
            } catch (err) {
                // Soft-fail: log and continue. The backend graceful
                // fallback handles the NULL columns, and the backfill
                // script can populate them later.
                // eslint-disable-next-line no-console
                console.warn('process_assets failed:', err?.message || err)
            }
        }

        if (created) {
            // Prepend so the grid surfaces the newest artwork first.
            artworks.value = [created, ...artworks.value]
        }

        // Sidebar refresh (generic delegation): a new Artwork row changes the
        // Decoration Library count the distributor sidebar shows. Emitted only
        // on the SUCCESS path -- an upload/create failure throws above and a
        // clear() mid-flight returns at the epoch check, so a doomed upload
        // fires no event. This is the fix for the live bug where the count was
        // correct only after a soft refresh.
        emitSidebarRefresh('decoration_library')
        if (priorApprovalError) throw priorApprovalError
        return created
    }

    /**
     * The caller's OWN artworks whose file is byte-identical to
     * ``contentHash`` (GET /artworks/duplicates; tenant-confined on the
     * server). Best-effort: any failure answers [] so a flaky lookup never
     * blocks an upload.
     */
    async function findDuplicateArtworks(contentHash) {
        try {
            const res = await api.get(
                `/artworks/duplicates?content_hash=${encodeURIComponent(contentHash)}`,
            )
            const rows = res?.data?.duplicates
            return Array.isArray(rows) ? rows : []
        } catch (_err) {
            return []
        }
    }

    /**
     * The team members an upload may name as having already approved a
     * stitch file ("already approved by team member", GH #4292): the active
     * members of ``distributorId`` (the caller's own tenant when null), as
     * ``[{ id, name, is_me }]``. ``is_me`` marks the signed-in member, the
     * picker's default. Errors propagate: the picker says the list could
     * not be loaded rather than offering an empty team.
     */
    async function fetchPriorApprovalTeamMembers(distributorId = null) {
        const params = distributorId == null ? {} : { distributor_id: distributorId }
        const res = await api.get('/artworks/prior-approval/team-members', {
            params,
        })
        const rows = res?.data?.members
        return {
            members: Array.isArray(rows) ? rows : [],
            // Only an Owner (or a Super Admin) may name another member;
            // anything but an explicit true reads as "only yourself".
            canNameOthers: res?.data?.can_name_others === true,
        }
    }

    /**
     * Transition an Artwork to a new lifecycle state via the F-3
     * custom endpoint. Returns the response payload
     * (``{artwork, transition}``) on success and updates the cached
     * Artwork in place so consumers re-render without a refetch.
     *
     * Errors propagate to the caller; transition failures (409 from
     * an illegal move) are NOT swallowed because the UI surface owns
     * the disabled-button + error-toast affordances.
     */
    async function transition(artworkId, toState, note = null) {
        const res = await api.post(`/artworks/${artworkId}/transition`, {
            to_state: toState,
            note,
        })
        const payload = res.data
        // Patch the local copy in place so consumers re-render.
        const idx = artworks.value.findIndex(
            (a) => String(a.id) === String(artworkId),
        )
        if (idx >= 0 && payload?.artwork) {
            artworks.value.splice(idx, 1, payload.artwork)
        } else if (payload?.artwork) {
            // Defensive: payload returned but row was missing locally
            // (cache miss). Add it so callers can see the new state.
            artworks.value.push(payload.artwork)
        }

        // Sidebar refresh (generic delegation): a lifecycle transition can flip
        // is_deleted (e.g. archive), which changes the Decoration Library count
        // the distributor sidebar shows. Emitted only after a successful
        // transition -- a rejected transition throws above (409 illegal move)
        // and never reaches here, so an illegal move fires no event.
        emitSidebarRefresh('decoration_library')
        return payload
    }

    /**
     * Cascade-delete an Artwork (+ its decorations / placements) via the
     * custom ``/artworks/<id>/cascade-delete/`` endpoint, then announce the
     * Decoration Library count change to the sidebar.
     *
     * Minimal store action for the sidebar-emit pattern -- owns the write +
     * emit; the component keeps its list/error UI (local-list pruning and the
     * 409/404/generic message strings stay in DecorationLibraryList). Full
     * store-ification of the decoration-library writes is tracked in
     * https://trello.com/c/g9K52FsA.
     *
     * Emit timing (mirrors uploadArtwork / transition):
     *   - 2xx success        -> a row was deleted, count dropped -> emit.
     *   - 404 already-gone   -> the row is gone server-side, count dropped
     *                           -> emit, then throw ``kind:'already_gone'`` so
     *                           the component prunes the local row (same as its
     *                           existing 404 branch).
     *   - 409 in_flight_orders -> NOTHING was deleted -> NO emit; throw
     *                           ``kind:'in_flight_orders'`` (original response
     *                           preserved so the component keeps its count /
     *                           blocked_placements message).
     *   - any other error    -> NO emit; throw ``kind:'error'`` for the generic
     *                           message.
     *
     * The typed error carries the original axios error on ``.response`` so the
     * component's message-mapping (and any future logging) is unchanged.
     */
    async function cascadeDelete(id) {
        try {
            await api.post(`/artworks/${id}/cascade-delete/`)
        } catch (err) {
            const status = err?.response?.status
            const data = err?.response?.data || {}
            if (status === 409 && data.error === 'in_flight_orders') {
                // Nothing deleted -- do NOT emit; let the component show its
                // in-flight-orders message and keep the row.
                throw taggedDeleteError('in_flight_orders', err)
            }
            if (status === 404) {
                // Already gone server-side: the count DID change, so emit,
                // then signal the component to prune (its existing 404 path).
                emitSidebarRefresh('decoration_library')
                throw taggedDeleteError('already_gone', err)
            }
            // Generic failure (incl. network errors with no response) --
            // no emit; the component shows its generic message.
            throw taggedDeleteError('error', err)
        }
        // Real delete succeeded: the Decoration Library count dropped.
        emitSidebarRefresh('decoration_library')
    }

    /**
     * Reset both collections + loading/error flags. Bumps the epoch so
     * in-flight ``fetch`` responses are ignored.
     */
    function clear() {
        epoch += 1
        artworks.value = []
        repositories.value = []
        error.value = null
        loading.value = false
    }

    return {
        // state
        artworks,
        repositories,
        loading,
        error,
        // lookups
        getArtworkById,
        getRepository,
        getArtworkForVariant,
        lifecycleStateForVariant,
        setLifecycleStateForVariant,
        // actions
        fetch,
        transition,
        uploadArtwork,
        findDuplicateArtworks,
        fetchPriorApprovalTeamMembers,
        cascadeDelete,
        clear,
    }
})

/**
 * The typed error ``uploadArtwork`` throws when the file is already in the
 * caller's library (Bug Intake #310). ``kind === 'duplicate'``;
 * ``duplicates`` carries the matches ({id, name, lifecycle_state,
 * used_by_count}). The message names the first match, so a surface that
 * does not special-case it still says why nothing was created.
 */
function duplicateUploadError(duplicates) {
    const first = duplicates[0]
    const err = new Error(
        `This file is already in your library as "${first.name}". ` +
            'Use the existing artwork, or upload it again as a new copy.',
    )
    err.kind = 'duplicate'
    err.duplicates = duplicates
    return err
}

/**
 * Wrap a cascade-delete axios error in a typed error the component maps to
 * its existing UI strings. ``kind`` is the seam ('in_flight_orders' |
 * 'already_gone' | 'error'); the original axios error is preserved on
 * ``.response`` so the component still reads status/data (count,
 * blocked_placements) exactly as it did when the POST lived inline.
 */
function taggedDeleteError(kind, cause) {
    const err = new Error(`cascade-delete: ${kind}`)
    err.kind = kind
    err.response = cause?.response
    err.cause = cause
    return err
}

/**
 * Build the absolute S3 / CDN URL for an uploaded image UUID.
 *
 * Mirrors the legacy ``getImage`` mixin (frontend/src/mixins/helpers.js)
 * so seeded fixtures and live uploads land at the same URL shape.
 * ``S3_IMAGE_BASE_URL`` already ends in ``images/``; we just append the
 * uuid.
 *
 * Exported for unit tests; consumers should prefer the store-level
 * ``uploadArtwork`` which calls this internally.
 */
export function buildImageUrl(uuid) {
    // Delegates to the shared helper so the S3/MinIO base URL is resolved from
    // ONE source of truth. The helper references process.env.S3_IMAGE_BASE_URL
    // directly (no `typeof process` guard) so webpack DefinePlugin inlines the
    // build-time value; the old inline guard defeated that inlining and images
    // fell back to a non-existent domain. See src/utils/imageBaseUrl.js.
    return sharedBuildImageUrl(uuid)
}

/**
 * Coerce a SAFRS or plain list response into a flat array of artwork
 * dicts. Handles three shapes seen across the codebase:
 *   - JSON:API: ``{data: [{type, id, attributes}, ...]}``
 *   - Bare list: ``[{...}, {...}]``
 *   - Custom-endpoint envelope: ``{artworks: [...]}``
 */
function normalizeArtworksResponse(body) {
    if (Array.isArray(body)) return body
    if (Array.isArray(body?.artworks)) return body.artworks
    if (Array.isArray(body?.data)) {
        return body.data.map((entry) => ({
            id: entry.id,
            ...(entry.attributes ?? {}),
        }))
    }
    // SAFRS single-resource POST response: {data: {type, id, attributes}}.
    // Returned by /api/artworks/ on create -- without this branch the
    // uploadArtwork action would yield null and skip the local prepend.
    if (body?.data && !Array.isArray(body.data) && body.data.id != null) {
        return [{ id: body.data.id, ...(body.data.attributes ?? {}) }]
    }
    return []
}

/**
 * Same shape coercer as artworks but for repositories. The F-3
 * ``/mine`` endpoint returns the custom envelope; SAFRS list returns
 * JSON:API. We accept both.
 */
function normalizeRepositoriesResponse(body) {
    if (Array.isArray(body)) return body
    if (Array.isArray(body?.repositories)) return body.repositories
    if (Array.isArray(body?.data)) {
        return body.data.map((entry) => ({
            id: entry.id,
            ...(entry.attributes ?? {}),
        }))
    }
    return []
}
